import ErrorBoundary from '@/components/common/ErrorBoundary';
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import Layout from '@/components/layout/Layout';
import Login from '@/pages/auth/Login';
import Dashboard from '@/pages/dashboard/Dashboard';

// Lazy load pages for better performance
const SupportInbox = React.lazy(() => import('@/pages/support/SupportInbox'));
const Users = React.lazy(() => import('@/pages/users/Users'));
const UserDetails = React.lazy(() => import('@/pages/users/UserDetails'));
const Transactions = React.lazy(() => import('@/pages/transactions/Transactions'));
const TransactionDetails = React.lazy(() => import('@/pages/transactions/TransactionDetails'));
const Analytics = React.lazy(() => import('@/pages/analytics/Analytics'));
const Reports = React.lazy(() => import('@/pages/reports/Reports'));
const System = React.lazy(() => import('@/pages/system/System'));
const AppVersionsPage = React.lazy(() => import('@/pages/system/AppVersionsPage'));
const Settings = React.lazy(() => import('@/pages/settings/Settings'));
const Alerts = React.lazy(() => import('@/pages/alerts/Alerts'));

// Coin System Pages
const PurchaseApprovals = React.lazy(() => import('@/pages/coins/PurchaseApprovals').then(m => ({ default: m.PurchaseApprovals })));
const WithdrawalApprovals = React.lazy(() => import('@/pages/coins/WithdrawalApprovals').then(m => ({ default: m.WithdrawalApprovals })));
const OfferManagement = React.lazy(() => import('@/pages/coins/OfferManagement').then(m => ({ default: m.OfferManagement })));
const BankAccountManagement = React.lazy(() => import('@/pages/coins/BankAccountManagement').then(m => ({ default: m.BankAccountManagement })));
const UPIAccountManagement = React.lazy(() => import('@/pages/coins/UPIAccountManagement').then(m => ({ default: m.UPIAccountManagement })));

// Referrals
const ReferralTracks = React.lazy(() => import('@/pages/referrals/ReferralTracks').then(m => ({ default: m.ReferralTracks })));
const LuckyDrawManagement = React.lazy(() => import('@/pages/promotions/LuckyDrawManagement').then(m => ({ default: m.LuckyDrawManagement })));
const NotificationCampaigns = React.lazy(() => import('@/pages/promotions/NotificationCampaigns').then(m => ({ default: m.NotificationCampaigns })));
const GamesStatus = React.lazy(() => import('@/pages/games/GamesStatus').then(m => ({ default: m.GamesStatus })));


// Loading component
const Loading: React.FC = () => (
    <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
    </div>
);

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { isAuthenticated, isLoading } = useAuthStore();

    if (isLoading) return <Loading />;
    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    return (
        <ErrorBoundary><React.Suspense fallback={<Loading />}>
            {children}
        </React.Suspense></ErrorBoundary>
    );
};

const App: React.FC = () => {
    return (
        <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />

            {/* Protected Routes */}
            <Route
                path="/*"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <Routes>
                                {/* Dashboard */}
                                <Route path="/dashboard" element={<Dashboard />} />

                                {/* User Management */}
                                <Route path="/users" element={<Users />} />
                                <Route path="/users/:id" element={<UserDetails />} />

                                {/* Transactions */}
                                <Route path="/transactions" element={<Transactions />} />
                                <Route path="/transactions/:id" element={<TransactionDetails />} />


                                {/* Coin System */}
                                <Route path="/coins/purchases" element={<PurchaseApprovals />} />
                                <Route path="/coins/withdrawals" element={<WithdrawalApprovals />} />
                                <Route path="/coins/offers" element={<OfferManagement />} />
                                <Route path="/coins/bank-accounts" element={<BankAccountManagement />} />
                                <Route path="/coins/upi-accounts" element={<UPIAccountManagement />} />

                                {/* Games */}
                                <Route path="/games/status" element={<GamesStatus />} />


                                {/* Referrals */}
                                <Route path="/referrals/tracks" element={<ReferralTracks />} />

                                {/* Promotions */}
                                <Route path="/promotions/lucky-draw" element={<LuckyDrawManagement />} />
                                <Route path="/promotions/notifications" element={<NotificationCampaigns />} />

                                {/* Analytics & Reports */}
                                <Route path="/support" element={<SupportInbox />} />
                                <Route path="/analytics" element={<Analytics />} />
                                <Route path="/reports" element={<Reports />} />

                                {/* System */}
                                <Route path="/system" element={<System />} />
                                <Route path="/system/app-versions" element={<AppVersionsPage />} />
                                <Route path="/alerts" element={<Alerts />} />
                                <Route path="/settings" element={<Settings />} />

                                {/* Default redirect */}
                                <Route path="/" element={<Navigate to="/dashboard" replace />} />

                                {/* 404 - Not Found */}
                                <Route
                                    path="*"
                                    element={
                                        <div className="flex items-center justify-center h-full">
                                            <div className="text-center">
                                                <h1 className="text-6xl font-bold text-slate-300 mb-4">404</h1>
                                                <h2 className="text-2xl font-semibold text-slate-700 mb-2">Page Not Found</h2>
                                                <p className="text-slate-500 mb-4">
                                                    The page you are looking for does not exist.
                                                </p>
                                                <button
                                                    onClick={() => window.history.back()}
                                                    className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors"
                                                >
                                                    Go Back
                                                </button>
                                            </div>
                                        </div>
                                    }
                                />
                            </Routes>
                        </Layout>
                    </ProtectedRoute>
                }
            />
        </Routes>
    );
};

export default App;
